<script setup lang="ts">
import { computed, provide, ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { Capacitor } from '@capacitor/core'
import { AppNavbar } from '@/components/app/app-navbar'
import AppFooter from './components/app/AppFooter.vue'
import BaseModal from '@/modal/BaseModal.vue'
import BetDock from "@/components/app/BetDock.vue"
import { KEY_IS_MOBILE } from "@/keys/keys"
import { useBetDock } from '@/composables/useBetDock'
import { useIsKeyboardOpen } from "@/composables/useIsKeyboardOpen"
import { useNavStore } from '@/store/modules/nav'
import { useAppInitialization } from '@/composables/useAppInitialization'
import { useProfilesStore } from '@/store/modules/userProfile'

import AppLoading from '@/components/app/AppLoading.vue'
import AppSplash from '@/components/app/AppSplash.vue'
import AppToasts from '@/components/app/AppToasts.vue'
import SamsungWarningModal from '@/components/app/SamsungWarningModal.vue'

const store = useStore()
const route = useRoute()
const navStore = useNavStore()
const profilesStore = useProfilesStore()
const { isKeyboardOpen } = useIsKeyboardOpen()
const { isVisible: isBetDockEnabled } = useBetDock()

const {
  isLoading,
  showLoading,
  isMobile,
  splashMode,
  showSplash
} = useAppInitialization()

provide(KEY_IS_MOBILE, isMobile)

const isBettingPath = computed(() => {
  const path = route.path
  return path.startsWith('/bet') || path.startsWith('/odds') || path.startsWith('/betpic')
})

onMounted(() => {
  profilesStore.loadLocal()
})

const hasBetInformation = computed(() => {
  const bbBetslip = store.getters['betBuilderModule/betslip']
  const bbHasSelections = (bbBetslip?.selectionGroups?.length > 0) || (bbBetslip?.legs?.length > 0)
  const bbLegsCount = store.getters['betBuilderModule/legs']?.length || 0

  const resolution = store.getters['betBuilderModule/resolution']
  const hasResolution = Object.keys(resolution || {}).length > 0

  const composeBetslip = store.getters['composeModule/betslip']
  const composeSelectionsCount = composeBetslip?.selectionGroups?.length || 0
  
  return bbHasSelections || bbLegsCount > 0 || composeSelectionsCount > 0 || hasResolution
})

const shouldShowBetDock = computed(() => {
  const hideOnFocus = (isMobile.value || Capacitor.isNativePlatform()) && isKeyboardOpen.value
  const hideOnShareModal = navStore.isShareModalVisible
  const isRestoring = store.getters['betBuilderModule/isRestoring']
  
  return isBetDockEnabled.value && hasBetInformation.value && !route.meta.hideNavbar && !hideOnFocus && !hideOnShareModal && !isRestoring
})
</script>

<template>
  <AppSplash
    v-if="showSplash"
    :show="showSplash"
    :mode="splashMode"
    :isLoading="showLoading"
    @animation-complete="showSplash = false"
  />

  <template v-else>
    <AppLoading :show="showLoading" />

    <div v-if="!showLoading" class="bg-app-background min-h-screen flex flex-col font-sans">
      <AppNavbar v-if="!route.meta.hideNavbar"/>

      <main class="flex-grow pt-24 md:pt-28 pb-safe px-safe max-md:pt-[calc(env(safe-area-inset-top)+4rem)]">
        <RouterView v-slot="{ Component }">
          <div class="container mx-auto">
            <component :is="Component" :key="route.path"/>
          </div>
        </RouterView>
      </main>

      <AppFooter v-if="!route.meta.hideNavbar" :open="!shouldShowBetDock && !isKeyboardOpen"/>
      
      <Teleport to="body" v-if="!route.meta.hideNavbar">
        <BetDock :visible="shouldShowBetDock" />
      </Teleport>
    </div>
  </template>

  <BaseModal/>
  <AppToasts />
  <SamsungWarningModal />
</template>

<style scoped>
:root {
  --ion-safe-area-top: env(safe-area-inset-top);
  --ion-safe-area-bottom: env(safe-area-inset-bottom);
}

ion-content {
  padding-top: var(--ion-safe-area-top);
  padding-bottom: var(--ion-safe-area-bottom);
}
</style>